import React, { useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { ArrowLeft, Clock, User, Edit } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import './KnowledgeArticlePage.css';

const STATUS_LABELS = {
    draft:     'Черновик',
    review:    'На проверке',
    revision:  'На доработке',
    published: 'Опубликован',
};

function formatDateTime(dt) {
    if (!dt) return '';
    const raw = String(dt).trim();
    const withZone = /[zZ]$|[+\-]\d{2}:\d{2}$/.test(raw)
        ? raw
        : `${raw.replace(' ', 'T')}Z`;
    return new Date(withZone).toLocaleString('ru-RU', {
        timeZone: 'Europe/Moscow',
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });
}

/**
 * Оборачивает все вхождения текста в HTML-строке тегом <mark>.
 * Работает с текстовыми нодами, не ломая теги.
 */
function highlightInHtml(html, keyword) {
    if (!keyword || !keyword.trim()) return html;

    const container = document.createElement('div');
    container.innerHTML = html;

    const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escaped})`, 'gi');

    function walk(node) {
        if (node.nodeType === Node.TEXT_NODE) {
            if (regex.test(node.textContent)) {
                const span = document.createElement('span');
                span.innerHTML = node.textContent.replace(regex, '<mark class="kb-highlight">$1</mark>');
                node.parentNode.replaceChild(span, node);
            }
        } else if (node.nodeType === Node.ELEMENT_NODE && node.tagName !== 'SCRIPT') {
            [...node.childNodes].forEach(walk);
        }
    }
    walk(container);
    return container.innerHTML;
}

const KnowledgeArticlePage = () => {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const contentRef = useRef(null);

    const highlight = searchParams.get('highlight') || '';

    const isAdmin = user?.role === 'admin';
    const isEditor = user?.is_knowledge_editor || isAdmin;

    const { data: article, isLoading, error } = useQuery(
        ['knowledgeArticle', id],
        async () => {
            const res = await axios.get(`${API_BASE_URL}/knowledge/${id}`, {
                withCredentials: true
            });
            return res.data;
        }
    );

    // После рендера статьи: скроллим к первому выделению
    useEffect(() => {
        if (!contentRef.current || !highlight) return;
        const firstMark = contentRef.current.querySelector('.kb-highlight');
        if (firstMark) {
            firstMark.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [article, highlight]);

    const canEdit = isAdmin || (isEditor && article?.user_id === user?.id && article?.status !== 'published');

    if (isLoading) {
        return (
            <div className="kb-article-page">
                <div className="kb-loading"><div className="spinner" /></div>
            </div>
        );
    }

    if (error || !article) {
        return (
            <div className="kb-article-page">
                <div className="kb-error">Статья не найдена</div>
            </div>
        );
    }

    const renderedContent = highlight
        ? highlightInHtml(article.content, highlight)
        : article.content;

    return (
        <div className="kb-article-page">
            <div className="kb-article-toolbar">
                <button className="kb-back-btn" onClick={() => navigate(-1)}>
                    <ArrowLeft size={18} />
                    Назад
                </button>
                {canEdit && (
                    <button
                        className="kb-edit-btn"
                        onClick={() => navigate(`/knowledge/editor/edit/${article.id}`)}
                    >
                        <Edit size={16} />
                        Редактировать
                    </button>
                )}
            </div>

            <article className="kb-article">
                <header className="kb-article-header">
                    {article.status !== 'published' && (
                        <span className={`kb-status-badge kb-status-${article.status}`}>
                            {STATUS_LABELS[article.status]}
                        </span>
                    )}
                    <h1 className="kb-article-title">{article.title}</h1>
                    <div className="kb-article-meta">
                        <span className="kb-meta-item">
                            <User size={14} />
                            {article.author_name}
                        </span>
                        <span className="kb-meta-item">
                            <Clock size={14} />
                            {formatDateTime(article.updated_at)}
                        </span>
                    </div>
                </header>

                <div
                    ref={contentRef}
                    className="kb-article-body"
                    dangerouslySetInnerHTML={{ __html: renderedContent }}
                />
            </article>
        </div>
    );
};

export default KnowledgeArticlePage;
